import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useBranding } from '../../lib/branding.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote } from '../../components/ui.js';
import type { BrandingAssetKind, BrandingSettings } from '../../lib/types.js';

type Kind = BrandingAssetKind;

const ASSETS: { kind: Kind; label: string; hint: string; field: keyof BrandingSettings['assets'] }[] = [
  { kind: 'logo', label: 'Logo', hint: 'Shown in the sidebar and on the login page. SVG or PNG, wide aspect.', field: 'logoUrl' },
  { kind: 'login-background', label: 'Login background', hint: 'Full-bleed image behind the sign-in card.', field: 'loginBackgroundUrl' },
  { kind: 'player-splash', label: 'Player splash', hint: 'Shown by the player shell while it loads.', field: 'playerSplashUrl' },
];

export function BrandingScreen(): ReactNode {
  const { refresh } = useBranding();
  const toast = useToast();
  const [data, setData] = useState<BrandingSettings | null>(null);
  const [error, setError] = useState<Error>();
  const [form, setForm] = useState({
    productName: '',
    accentColor: '#2563eb',
    supportUrl: '',
    emailFromName: '',
    emailFooter: '',
  });
  const [saving, setSaving] = useState(false);

  function load(d: BrandingSettings): void {
    setData(d);
    setForm({
      productName: d.settings.productName ?? '',
      accentColor: d.settings.accentColor ?? d.defaults.accentColor,
      supportUrl: d.settings.supportUrl ?? '',
      emailFromName: d.settings.emailFromName ?? '',
      emailFooter: d.settings.emailFooter ?? '',
    });
  }

  useEffect(() => {
    api.brandingSettings().then(load).catch((e: unknown) => setError(e instanceof Error ? e : new Error(String(e))));
  }, []);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const updated = await api.updateBranding({
        productName: form.productName || null,
        accentColor: form.accentColor || null,
        supportUrl: form.supportUrl || null,
        emailFromName: form.emailFromName || null,
        emailFooter: form.emailFooter || null,
      });
      load(updated);
      refresh();
      toast.push('Branding saved', 'success');
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <Page title="Branding"><ErrorNote error={error} /></Page>;
  if (!data) return <Page title="Branding"><Loading what="branding" /></Page>;

  return (
    <Page
      title="Branding"
      actions={
        <button className="btn primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      }
    >
      <p className="muted small" style={{ marginBottom: 16 }}>
        White-label the console, sign-in page, player splash and alert emails. Leave a field blank to
        use the install default.
      </p>

      <div className="card card-pad stack" style={{ maxWidth: 560 }}>
        <label className="field">
          Product name
          <input
            value={form.productName}
            placeholder={data.defaults.productName}
            onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))}
          />
        </label>
        <label className="field">
          Accent colour
          <span className="row" style={{ gap: 8 }}>
            <input
              type="color"
              value={form.accentColor}
              onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))}
              style={{ width: 48, padding: 2 }}
            />
            <input
              value={form.accentColor}
              onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))}
              style={{ maxWidth: 120 }}
            />
            <button
              className="btn primary small"
              type="button"
              onClick={() => document.documentElement.style.setProperty('--accent', form.accentColor)}
            >
              Preview
            </button>
          </span>
        </label>
        <label className="field">
          Support URL
          <input
            value={form.supportUrl}
            placeholder="https://help.example.com"
            onChange={(e) => setForm((f) => ({ ...f, supportUrl: e.target.value }))}
          />
        </label>
        <label className="field">
          Email “from” name
          <input
            value={form.emailFromName}
            placeholder={form.productName || data.defaults.productName}
            onChange={(e) => setForm((f) => ({ ...f, emailFromName: e.target.value }))}
          />
        </label>
        <label className="field">
          Email footer
          <textarea
            rows={2}
            value={form.emailFooter}
            onChange={(e) => setForm((f) => ({ ...f, emailFooter: e.target.value }))}
            style={{ width: '100%', fontFamily: 'inherit' }}
          />
        </label>
      </div>

      <div style={{ height: 20 }} />

      <div className="card card-pad stack" style={{ maxWidth: 560 }}>
        {ASSETS.map((a) => (
          <AssetRow
            key={a.kind}
            label={a.label}
            hint={a.hint}
            url={data.assets[a.field]}
            onUpload={async (file) => {
              const updated = await api.uploadBrandingAsset(a.kind, file);
              load(updated);
              refresh();
            }}
            onRemove={async () => {
              const updated = await api.deleteBrandingAsset(a.kind);
              load(updated);
              refresh();
            }}
          />
        ))}
      </div>
    </Page>
  );
}

function AssetRow({
  label,
  hint,
  url,
  onUpload,
  onRemove,
}: {
  label: string;
  hint: string;
  url: string | null;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}): ReactNode {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(file: File | undefined): Promise<void> {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(file);
      toast.push(`${label} updated`, 'success');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="row between" style={{ alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div className="faint small">{hint}</div>
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        {url ? (
          <img
            src={url}
            alt={label}
            style={{ maxHeight: 36, maxWidth: 120, background: '#8884', borderRadius: 4 }}
          />
        ) : (
          <span className="faint small">not set</span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void pick(e.target.files?.[0])}
        />
        <button className="btn small" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? '…' : url ? 'Replace' : 'Upload'}
        </button>
        {url ? (
          <button className="btn small danger" disabled={busy} onClick={() => void onRemove()}>
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}
